import {
  getLocalStorageMeasureProgressRushState,
  setLocalStorageMeasureProgressRushState,
} from "api/dropsLocalStorage";
import { ExpanderButton } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/MeasureProgress/components/ExpanderButton";
import { InfoContent } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/MeasureProgress/components/InfoContent";
import { ProgressionBody } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/MeasureProgress/components/ProgressionBody";
import DecisionSupportContext from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/DecisionSupportContext";
import { AltTransStretchCoverage } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/types";
import { useAlternativeTransportMeasureProgress } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/useAlternativeTransportMeasureProgress";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { SectionStateContent } from "shared/components/states/SectionStateContent";
import { InfoHeader } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/InfoHeader";
import { Flex, tokens, VStack } from "shared/components/Spor";
import { Nullish } from "shared/utils/objectUtils";
import { useTheme } from "styled-components";

interface MeasureProgressProps {
  showAllStretches: boolean;
  setShowAllStretches: Dispatch<SetStateAction<boolean>>;
  stretch: AltTransStretchCoverage | null;
  setStretch: (stretch: AltTransStretchCoverage | null) => void;
  incidentId: Nullish<string>;
}

export const MeasureProgress = ({
  showAllStretches,
  setShowAllStretches,
  stretch,
  setStretch,
  incidentId,
}: MeasureProgressProps) => {
  const theme = useTheme();
  const { horizonFromTime, horizonToTime } = useContext(DecisionSupportContext);

  const [isRush, setIsRush] = useState(false);

  const toggleIsRush = useCallback(() => {
    setIsRush((prev) => {
      if (incidentId)
        setLocalStorageMeasureProgressRushState(incidentId, !prev);
      return !prev;
    });
  }, [incidentId]);

  const { data, status, refetch, isRefetching } =
    useAlternativeTransportMeasureProgress(
      incidentId,
      horizonFromTime,
      horizonToTime,
      isRush,
    );

  const activeStretches = useMemo(
    () => (data?.stretchCoverages ? data.stretchCoverages : []),
    [data],
  );

  useEffect(() => {
    if (incidentId) {
      setIsRush(getLocalStorageMeasureProgressRushState(incidentId));
    }
  }, [incidentId]);

  const sectionState = useMemo(
    () => ({
      fetchStatus: isRefetching ? "pending" : status,
      isEmpty: activeStretches.length === 0,
      onRetry: refetch,
    }),
    [activeStretches.length, isRefetching, refetch, status],
  );

  return (
    <VStack
      backgroundColor={theme.colorBackgroundSecondary}
      borderRadius={tokens.default.size.spacing[1]}
      flex="1"
      overflow="hidden"
      minHeight="330px"
      height="100%"
      p={2}
      gap={1}
    >
      <InfoHeader
        title="Progresjon for tiltak"
        alignment="right"
        isRush={isRush}
        toggleIsRush={toggleIsRush}
        iconColor={theme.colorIconSecondary}
        tooltipWidth="360px"
      >
        <InfoContent />
      </InfoHeader>
      <SectionStateContent state={sectionState}>
        <Flex direction="column" width="100%" gap="3">
          <ProgressionBody
            showAllStretches={showAllStretches}
            stretch={stretch}
            setStretch={setStretch}
            allStretchCoverages={activeStretches}
            totalCoverage={data?.totalCoverage}
            peakPassengersRequiringTransport={
              data?.peakPassengersRequiringTransport
            }
          />
          <ExpanderButton
            showAllStretches={showAllStretches}
            setShowAllStretches={setShowAllStretches}
            stretchCount={activeStretches.length}
          />
        </Flex>
      </SectionStateContent>
    </VStack>
  );
};
